/**
 * API client — ALL calls to PRIMARY API
 * Single-vendor luxury watch marketplace
 */
const rc = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
const PRIMARY_API = rc.PRIMARY_API_URL || process.env.REACT_APP_PRIMARY_API_URL || '';
const API_BASE = PRIMARY_API ? PRIMARY_API.replace(/\/+$/, '') : 'https://premium-watch-store-3.preview.emergentagent.com';

const getAuthToken = () => {
  try {
    const raw = sessionStorage.getItem('vwatch_admin_auth');
    if (!raw) return null;
    return JSON.parse(raw).token || null;
  } catch (e) { console.warn('Token read error:', e.message); return null; }
};

const api = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const resp = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  if (resp.status === 404) { console.warn(`[API 404] ${endpoint}`); return null; }
  if (!resp.ok) { const err = await resp.text(); throw new Error(err || `${resp.status}`); }
  return resp.json();
};

const apiJSON = (endpoint, method, body) =>
  api(endpoint, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

// ===== AUTH =====
export const authAPI = {
  login: async (email, password) => {
    const resp = await fetch(`${API_BASE}/api/auth/admin/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    });
    const data = await resp.json();
    if (!resp.ok || data.error) throw new Error(data.message || data.error || 'Login failed');
    return data;
  },
  getCurrentUser: () => api('/api/auth/me'),
};

// ===== BRANDS =====
export const brandsAPI = {
  getAll: async () => {
    const data = await api('/api/brands');
    if (!data || !Array.isArray(data)) return [];
    return data.map(b => ({
      id: b.id || b._id, name: b.name, country: b.country || '',
      founded: b.founded || '', logo: b.logo || b.image || '',
      featured: b.featured || false, listingsCount: b.watchCount || b.listingsCount || 0,
      description: b.description || '', createdAt: b.createdAt,
    }));
  },
  create: (data) => apiJSON('/api/admin/brands', 'POST', data),
  update: (id, data) => apiJSON(`/api/admin/brands/${id}`, 'PATCH', data),
  delete: (id) => api(`/api/admin/brands/${id}`, { method: 'DELETE' }),
};

// ===== CATEGORIES =====
const LOCAL_API = rc.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '';
const LOCAL_BASE = LOCAL_API ? LOCAL_API.replace(/\/+$/, '') : '';

const localApi = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const resp = await fetch(`${LOCAL_BASE}${endpoint}`, { ...options, headers });
  if (resp.status === 404) return null;
  if (!resp.ok) { const err = await resp.text(); throw new Error(err || `${resp.status}`); }
  return resp.json();
};

const localJSON = (endpoint, method, body) =>
  localApi(endpoint, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export const categoriesAPI = {
  getRaw: () => api('/api/categories'),
  getAll: async () => {
    const data = await api('/api/categories');
    if (!data) return [];
    if (Array.isArray(data)) return data.map(c => ({
      id: c.id || c._id, name: c.name, slug: c.slug || c.name?.toLowerCase().replace(/\s+/g, '-'),
      description: c.description || '', featured: c.featured || false,
      listingsCount: c.listingsCount || 0, createdAt: c.createdAt,
    }));
    // If API returns {brands, conditions, materials, locations}
    const conditions = data.conditions || [];
    return conditions.map((name, i) => ({
      id: `cat-${i}`, name, slug: name.toLowerCase().replace(/\s+/g, '-'),
      description: '', featured: false, listingsCount: 0,
    }));
  },
  getLocal: () => localApi('/api/admin/categories'),
  createLocal: (data) => localJSON('/api/admin/categories', 'POST', data),
  updateLocal: (id, data) => localJSON(`/api/admin/categories/${id}`, 'PATCH', data),
  deleteLocal: (id) => localApi(`/api/admin/categories/${id}`, { method: 'DELETE' }),
};

// ===== LISTINGS =====
export const listingsAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.append('search', params.search);
    if (params.brand) qs.append('brand', params.brand);
    if (params.category) qs.append('category', params.category);
    qs.append('limit', '50');
    const data = await api(`/api/listings?${qs}`);
    if (!data) return [];
    const listings = data.listings || (Array.isArray(data) ? data : []);
    return listings.map(l => ({
      id: l.id || l._id, brand: l.brand || '', model: l.model || l.title || '',
      ref: l.refNumber || l.ref || '', year: l.year || '', condition: l.condition || '',
      category: l.category || '', price: l.price || 0, currency: l.currency || 'USD',
      caseSize: l.caseSize || '', material: l.material || '', movement: l.movement || '',
      dialColor: l.dialColor || '', strap: l.strap || '', boxPapers: l.boxPapers || '',
      description: l.description || '', image: l.images?.[0] || '', images: l.images || [],
      status: l.status || 'approved', addedAt: l.createdAt,
    }));
  },
  getById: async (id) => {
    const l = await api(`/api/listings/${id}`);
    if (!l) return null;
    return {
      id: l.id || l._id, brand: l.brand || '', model: l.model || l.title || '',
      ref: l.refNumber || l.ref || '', year: l.year || '', condition: l.condition || '',
      category: l.category || '', price: l.price || 0, currency: l.currency || 'USD',
      caseSize: l.caseSize || '', material: l.material || '', movement: l.movement || '',
      dialColor: l.dialColor || '', strap: l.strap || '', boxPapers: l.boxPapers || '',
      description: l.description || '', image: l.images?.[0] || '', images: l.images || [],
      status: l.status || 'approved',
    };
  },
  create: (data) => {
    const images = (data.images || []).filter(i => typeof i === 'string' && (i.startsWith('http') || (i.startsWith('data:') && i.length < 200000)));
    return apiJSON('/api/admin/listings', 'POST', {
      title: `${data.brand || ''} ${data.model || ''}`.trim(), brand: data.brand, model: data.model,
      refNumber: data.ref || '', year: parseInt(data.year) || new Date().getFullYear(),
      condition: data.condition || 'New', price: parseFloat(data.price) || 0,
      currency: 'USD', location: data.location || 'International',
      caseSize: data.caseSize || '', material: data.material || '', movement: data.movement || '',
      dialColor: data.dialColor || '', strap: data.strap || '', boxPapers: data.boxPapers || '',
      description: data.description || '', images, status: 'active',
    });
  },
  update: (id, data) => {
    const images = (data.images || []).filter(i => typeof i === 'string' && (i.startsWith('http') || (i.startsWith('data:') && i.length < 200000)));
    return apiJSON(`/api/admin/listings/${id}`, 'PATCH', {
      title: `${data.brand || ''} ${data.model || ''}`.trim(), brand: data.brand, model: data.model,
      refNumber: data.ref || '', condition: data.condition, price: parseFloat(data.price) || 0,
      caseSize: data.caseSize, material: data.material, movement: data.movement,
      dialColor: data.dialColor, strap: data.strap, boxPapers: data.boxPapers,
      description: data.description, images,
    });
  },
  delete: (id) => api(`/api/listings/${id}`, { method: 'DELETE' }),
};

// ===== ORDERS =====
export const ordersAPI = {
  getAll: async (status = '') => {
    const data = await api(`/api/admin/orders${status ? `?status=${status}` : ''}`);
    if (!data) return [];
    const orders = Array.isArray(data) ? data : data.orders || [];
    return orders.map(o => ({
      id: o.id || o._id, orderNumber: o.orderNumber || '',
      buyer: o.customerName || o.userEmail || o.buyerName || 'Customer',
      item: o.listingTitle || o.item || '', total: o.totalPrice || o.totalAmount || 0,
      currency: o.currency || 'USD', status: o.status || 'paid',
      paidAt: o.purchasedAt || o.paidAt || o.createdAt,
    }));
  },
  updateStatus: (id, status) => apiJSON(`/api/admin/orders/${id}/status`, 'PATCH', { status }),
  create: (data) => apiJSON('/api/admin/orders', 'POST', data),
};

// ===== USERS =====
let _usersCache = null; let _usersCacheTime = 0;
export const getUsersMap = async () => {
  if (_usersCache && (Date.now() - _usersCacheTime) < 60000) return _usersCache;
  try {
    const users = await api('/api/admin/users');
    if (Array.isArray(users)) { _usersCache = {}; users.forEach(u => { _usersCache[u.id] = u; }); _usersCacheTime = Date.now(); }
  } catch (e) { console.error('Failed to fetch users cache:', e.message); }
  return _usersCache || {};
};
export const usersAPI = {
  getAll: async (search = '') => {
    const data = await api(`/api/admin/users${search ? `?search=${search}` : ''}`);
    return data || [];
  },
};

// ===== STATS =====
export const statsAPI = {
  getOverview: async () => {
    const d = await api('/api/stats/overview');
    if (!d) return { customers: 0, listings: 0, brands: 0, categories: 0, revenue: [] };
    return {
      customers: d.totalUsers || d.total_users || 0,
      listings: d.totalWatches || d.total_listings || 0,
      brands: d.totalBrands || 0,
      categories: d.totalCategories || 0,
      totalOrders: d.totalOrders || d.total_orders || 0,
      totalRevenue: d.totalRevenue || d.total_revenue || 0,
      inventoryValue: d.totalInventoryValue || d.inventory_value || 0,
      avgPrice: d.avgWatchPrice || d.avg_watch_price || 0,
    };
  },
  getRevenue: async () => {
    const d = await api('/api/stats/sales-trend?period=210');
    if (!d || !Array.isArray(d)) return [];
    // Group by month for 7-month revenue chart
    const months = {};
    d.forEach(p => {
      const m = new Date(p.date).toLocaleDateString('en-US', { month: 'short' });
      months[m] = (months[m] || 0) + (p.value || p.revenue || 0);
    });
    return Object.entries(months).slice(-7).map(([month, revenue]) => ({ month, revenue }));
  },
  getBrandDistribution: async () => {
    const d = await api('/api/stats/brand-distribution');
    if (!d || !Array.isArray(d)) return [];
    return d.map(b => ({ name: b.brand || b.name, value: b.count || b.value || 0 }));
  },
  getPriceRanges: async () => {
    const d = await api('/api/stats/price-ranges');
    if (!d || !Array.isArray(d)) return [];
    return d.map(r => ({ label: r.range || r.label, value: r.count || r.value || 0 }));
  },
};

// ===== SETTINGS =====
export const settingsAPI = {
  get: () => api('/api/settings'),
  update: (data) => apiJSON('/api/settings', 'PATCH', data),
};

const getConvWatchStatus = (status) => {
  if (status === 'sold') return 'purchased';
  if (status === 'cancelled') return 'cancelled';
  return 'active';
};

const getSenderRole = (m) => {
  if (m.type === 'system') return 'admin';
  if (m.senderRole === 'ADMIN' || m.senderName === 'Admin User') return 'admin';
  return 'user';
};

// ===== MESSAGING =====
export const messagingAPI = {
  getConversations: async () => {
    const [d, usersMap] = await Promise.all([api('/api/conversations'), getUsersMap()]);
    if (!d || !Array.isArray(d)) return [];
    const groups = {};
    d.forEach(c => {
      const bid = c.buyerId || 'unknown';
      const buyer = usersMap[bid] || {};
      const name = c.buyerName || buyer.name || buyer.email?.split('@')[0] || bid.substring(0, 8);
      if (!groups[bid]) {
        groups[bid] = { id: c.id, buyerId: bid, user_name: name, user_email: c.buyerEmail || buyer.email || '',
          conversations: [], watches: [], unread_admin: 0, last_message: '', last_message_at: '', status: 'active', created_at: c.createdAt };
      }
      const g = groups[bid];
      g.conversations.push(c);
      g.unread_admin += c.unreadCount || 0;
      const t = c.lastMessageAt || c.updatedAt || '';
      if (t > g.last_message_at) { g.last_message_at = t; g.last_message = c.lastMessage || ''; g.id = c.id; }
      if (c.listingTitle) {
        g.watches.push({ conv_id: c.id, title: c.listingTitle, ref: c.listingRef || '', price: c.listingPrice || 0,
          image: c.listingImage || '', watch_ref: { name: c.listingTitle, price: c.listingPrice, image: c.listingImage },
          status: getConvWatchStatus(c.status),
          last_message: c.lastMessage || '', unread: c.unreadCount || 0 });
      }
    });
    return Object.values(groups).sort((a, b) => (b.last_message_at || '').localeCompare(a.last_message_at || ''))
      .map(g => ({ ...g, watch_ref: g.watches[0]?.watch_ref || null, status: g.watches.some(w => w.status === 'active') ? 'active' : 'sold' }));
  },
  getUnreadCount: async () => { const d = await api('/api/conversations/unread/count'); return { unread: d?.count ?? d?.unread ?? 0 }; },
  getMessages: async (convId) => {
    const d = await api(`/api/conversations/${convId}/messages`);
    if (!d) return [];
    const msgs = Array.isArray(d) ? d : d.messages || [];
    return msgs.map(m => ({
      id: m.id || m._id, conversation_id: m.conversationId, sender_id: m.senderId,
      sender_role: getSenderRole(m),
      sender_name: m.senderName || (m.type === 'system' ? 'System' : 'User'),
      content: m.content || m.text || '', watch_ref: m.watchRef || null, created_at: m.createdAt,
    }));
  },
  markRead: (convId) => api(`/api/conversations/${convId}/read`, { method: 'POST' }),
  sendMessage: (convId, content) => apiJSON(`/api/conversations/${convId}/messages`, 'POST', { content }),
  markPurchased: (convId) => api(`/api/admin/conversations/${convId}/mark-purchased`, { method: 'POST' }),
  cancelOrder: (convId) => api(`/api/admin/conversations/${convId}/cancel-order`, { method: 'POST' }),
  reopenDiscussion: (convId) => api(`/api/admin/conversations/${convId}/reopen`, { method: 'POST' }),
  clearCustomer: (convId) => api(`/api/admin/conversations/${convId}/clear-customer`, { method: 'POST' }),
};

// Keep old exports for backward compat
export const watchesAPI = listingsAPI;
